import { FC, useEffect, useState } from 'react';

import {
  clearGiftStorePreview,
  getGiftStorePreview,
} from '@shared/utils/giftStorePreview.util';
import { useLocation } from 'react-router-dom';

/**
 * 관리자 미리보기 모드 표시 배너 (스토어 전 페이지 공통, NavBar 아래 고정 — 하단 구매 바를 가리지 않게 위쪽)
 *
 * 실제 상가 부고를 빌려 여는 화면이라, 실제 고객 화면과 헷갈리거나 주문을 넣지 않도록
 * 어느 상조 미리보기인지 상시 노출한다. 조회 로그 미기록·결제 차단은 게이트/로그 쪽에서 처리.
 */
const MINIMIZED_KEY = 'giftStorePreview.minimized';

export const GiftStorePreviewBanner: FC = function GiftStorePreviewBanner() {
  const location = useLocation();
  const [ctx, setCtx] = useState(() => getGiftStorePreview());
  // 배너가 카테고리 칩 줄을 가려서 접을 수 있게 한다. 접으면 우측 가장자리의 작은 표식만 남긴다
  // (미리보기 중이라는 사실 자체는 계속 보여야 실제 고객 화면과 헷갈리지 않는다). 탭 단위로 기억.
  const [minimized, setMinimized] = useState(() => {
    try {
      return sessionStorage.getItem(MINIMIZED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleMinimized = (next: boolean) => {
    setMinimized(next);
    try {
      sessionStorage.setItem(MINIMIZED_KEY, next ? '1' : '0');
    } catch {
      // noop
    }
  };

  // 게이트가 진입 시점(렌더 중)에 표시를 남기므로 이동할 때마다 다시 확인
  useEffect(() => {
    setCtx(getGiftStorePreview());
  }, [location]);

  if (!ctx) return null;

  const handleExit = () => {
    clearGiftStorePreview();
    window.close();
    // 직접 연 탭이라 닫히지 않는 경우 상품관리로 돌아간다
    window.location.href = '/gift-seller/item';
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => toggleMinimized(false)}
        aria-label="미리보기 안내 펼치기"
        className="fixed right-0 top-1/2 z-40 -translate-y-1/2 rounded-l-lg bg-gray-900/90 px-1.5 py-3 text-[11px] font-semibold leading-tight text-white shadow-lg [writing-mode:vertical-rl]"
      >
        미리보기
      </button>
    );
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-16 z-40 flex justify-center px-3">
      <div className="pointer-events-auto flex items-center gap-3 rounded-lg bg-gray-900/90 px-3 py-2 text-white shadow-lg">
        <p className="text-xs leading-tight">
          <span className="font-semibold">미리보기</span>
          {ctx.brandName ? ` · ${ctx.brandName}` : ''}
          <span className="block text-gray-300">
            {ctx.test
              ? '테스트 상가 · 결제 가능 (테스트 후 주문 취소)'
              : '조회 기록 안 남음 · 결제 불가'}
          </span>
        </p>
        <button
          onClick={() => toggleMinimized(true)}
          className="whitespace-nowrap rounded-md border border-white/40 px-2 py-1 text-xs font-medium text-white hover:bg-white/10"
        >
          접기
        </button>
        <button
          onClick={handleExit}
          className="whitespace-nowrap rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-900 hover:bg-gray-100"
        >
          종료
        </button>
      </div>
    </div>
  );
};
